import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Mail, Clock, Truck, MapPin, CreditCard, Coins, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Seo } from "../components/Seo";
import { EtransferSteps } from "../components/EtransferSteps";
import { SHIPPING_METHODS, shippingMethodLabel } from "../components/ShippingMethodPicker";
import { openMintagePayPopup, buyerFromOrder } from "../lib/mintagePay";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const canceled = searchParams.get("canceled");
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(!!sessionId);
  const [payingNow, setPayingNow] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get(`/orders/${id}`), api.get("/settings")])
      .then(([o, s]) => { setOrder(o.data); setSettings(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, user]);

  // Poll the payment status when returning from the payment window
  useEffect(() => {
    if (!sessionId || !user) return;
    let stopped = false;
    let tries = 0;
    const tick = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const { data: o } = await api.get(`/orders/${id}`);
          if (!stopped) { setOrder(o); setConfirming(false); }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          if (!stopped) setConfirming(false);
          return;
        }
      } catch { /* transient — keep polling */ }
      if (tries < 15) setTimeout(tick, 2500);
      else if (!stopped) setConfirming(false);
    };
    tick();
    return () => { stopped = true; };
  }, [sessionId, user, id]);

  const payNow = useCallback(async () => {
    setPayingNow(true);
    try {
      const { data } = await api.post("/payments/checkout", { order_id: id, origin_url: window.location.origin });
      openMintagePayPopup(data.checkout_url, data.return_path, buyerFromOrder(order, user?.email));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the payment. Please try again.");
    } finally {
      setPayingNow(false);
    }
  }, [id, order, user]);

  if (authLoading || (user && loading)) {
    return (
      <div className="relative z-10 flex justify-center py-40">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="relative z-10 max-w-md mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-xl sm:text-2xl uppercase tracking-wide text-[#1A1A1A]">Sign in to view your order</h1>
        <p className="text-sm text-[#6E675F] mt-3">Use the account you placed the order with.</p>
        <button data-testid="order-signin-button" onClick={() => navigate(`/login?next=/order/${id}`)} className="mt-8 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-10 py-4 rounded-none hover:bg-[#75665A] transition-colors">
          Sign In
        </button>
      </div>
    );
  }
  if (!order) {
    return <p className="relative z-10 text-center text-[#6E675F] py-40">Order not found.</p>;
  }

  const isCard = order.payment_method === "card" || order.payment_method === "crypto";
  const isCrypto = order.payment_method === "crypto";
  const PayIcon = isCrypto ? Coins : CreditCard;
  const awaitingCard = order.status === "awaiting_payment";

  return (
    <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 py-16">
      <Seo title="Order Confirmation" path="/order" noindex />
      <div className="text-center mb-12">
        <CheckCircle2 className="w-14 h-14 text-[#8C7B6B] mx-auto mb-5" />
        <h1 className="font-display text-lg uppercase tracking-wide sm:text-3xl uppercase tracking-wide text-[#1A1A1A]" data-testid="order-confirmation-heading">
          {isCard && order.status === "verified" ? "Payment Received" : "Order Placed"}
        </h1>
        <p className="text-base text-[#6E675F] mt-3">
          Order <strong className="text-[#1A1A1A]" data-testid="order-number">{order.order_number}</strong>
        </p>
        <div className="mt-4 flex justify-center"><StatusBadge status={order.status} /></div>
      </div>

      {awaitingCard && confirming && (
        <div className="border-2 border-[#8C7B6B] rounded-lg p-8 bg-white mb-10 text-center" data-testid="payment-confirming-box">
          <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A]">Confirming Your Payment…</h2>
          <p className="text-sm text-[#6E675F] mt-2">This usually takes a few seconds. Please don't close this page.</p>
        </div>
      )}

      {awaitingCard && !confirming && (
        <div className="border-2 border-amber-400 rounded-lg p-8 bg-white mb-10" data-testid="awaiting-payment-box">
          <div className="flex items-center gap-3 mb-4">
            {canceled ? <AlertTriangle className="w-6 h-6 text-amber-600" /> : <PayIcon className="w-6 h-6 text-[#8C7B6B]" />}
            <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A]">
              {canceled ? "Payment Not Completed" : isCrypto ? "Complete Your Crypto Payment" : "Complete Your Card Payment"}
            </h2>
          </div>
          <p className="text-sm text-[#6E675F] leading-relaxed">
            {canceled
              ? "Your payment was cancelled — no charge was made. Your order is saved, and you can pay whenever you're ready."
              : isCrypto
                ? "Your order is saved but not paid yet. Complete the crypto payment to confirm it."
                : "Your order is saved but not paid yet. Complete the debit card payment to confirm it. Credit cards will not work."}
          </p>
          <button
            data-testid="pay-now-button"
            onClick={payNow}
            disabled={payingNow}
            className="mt-6 inline-flex items-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-8 py-3.5 rounded-none hover:bg-[#75665A] transition-colors disabled:opacity-60"
          >
            <PayIcon className="w-4 h-4" /> {payingNow ? "Opening…" : isCrypto ? `Pay $${order.total.toFixed(2)} with Crypto` : `Pay $${order.total.toFixed(2)} by Card`}
          </button>
        </div>
      )}

      {isCard && order.status === "verified" && (
        <div className="border-2 border-emerald-300 rounded-lg p-6 bg-emerald-50/50 mb-10 flex items-start gap-3" data-testid="card-paid-box">
          <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0 mt-0.5" />
          <p className="text-sm text-[#1A1A1A] leading-relaxed">
            Your {isCrypto ? "crypto" : "card"} payment of <strong>${order.total.toFixed(2)} CAD</strong> was received and your order is confirmed.
            We're preparing it for shipment — you'll get an email with tracking once it ships.
          </p>
        </div>
      )}

      {order.status === "pending" && !isCard && (
        <div className="border-2 border-[#8C7B6B] rounded-lg p-8 bg-white mb-10" data-testid="confirmation-etransfer-instructions">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-6 h-6 text-[#8C7B6B]" />
            <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A]">Complete Your Payment</h2>
          </div>
          <EtransferSteps
            testidPrefix="confirmation"
            steps={settings?.etransfer_steps || []}
            values={{
              AMOUNT: { text: `$${order.total.toFixed(2)} CAD`, testid: "confirmation-amount", className: "text-[#8C7B6B] text-xl" },
              ETRANSFER_EMAIL: { text: settings?.etransfer_email || "…", testid: "confirmation-etransfer-email" },
              RECIPIENT_NAME: { text: settings?.etransfer_name || "" },
              YOUR_EMAIL: { text: order.email },
              ORDER_NUMBER: { text: order.order_number, testid: "confirmation-order-number" },
            }}
          />
          {settings?.instructions && (
            <p className="text-sm text-[#6E675F] mt-6 pt-6 border-t border-[#DDD7CF] whitespace-pre-line">{settings.instructions}</p>
          )}
          <div className="flex items-center gap-2 mt-6 text-sm text-[#6E675F]">
            <Clock className="w-4 h-4 text-[#8C7B6B]" />
            Your order will be verified once payment is received.
          </div>
        </div>
      )}

      {order.shipping_address && (
        <div className="border border-[#DDD7CF] rounded-lg p-6 bg-white mb-10" data-testid="order-shipping-address">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Shipping To
          </p>
          <div className="text-base text-[#1A1A1A] mt-3 leading-relaxed">
            <p data-testid="shipping-address-name">{order.shipping_address.name}</p>
            <p>{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</p>
            <p>{order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.postal_code}</p>
            {order.shipping_address.phone && <p className="text-sm text-[#6E675F] mt-1">Phone: {order.shipping_address.phone}</p>}
          </div>
          <p className="text-sm text-[#6E675F] mt-3 pt-3 border-t border-[#DDD7CF] flex items-center gap-2" data-testid="confirmation-shipping-method">
            <Truck className="w-4 h-4 text-[#8C7B6B]" />
            <span>
              <strong className="text-[#1A1A1A]">{shippingMethodLabel(order.shipping_method)} delivery</strong>
              {" "}· {SHIPPING_METHODS[order.shipping_method]?.carrier || SHIPPING_METHODS.standard.carrier}
            </span>
          </p>
        </div>
      )}

      {order.tracking_number && (
        <div className="border border-[#DDD7CF] rounded-lg p-6 bg-white mb-10 flex items-center gap-4" data-testid="order-tracking-box">
          <Truck className="w-6 h-6 text-[#8C7B6B] shrink-0" />
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F]">Tracking Number</p>
            <p className="text-lg font-medium text-[#1A1A1A] mt-1" data-testid="order-tracking-number">{order.tracking_number}</p>
          </div>
        </div>
      )}

      <div className="border border-[#DDD7CF] rounded-lg p-6 bg-white">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-5">Items</p>
        {order.items.map((i) => (
          <div key={i.product_id} className="flex justify-between text-sm py-2">
            <span className="text-[#1A1A1A]">{i.name} {i.dosage} × {i.quantity}</span>
            <span className="text-[#6E675F]">${(i.price * i.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="border-t border-[#DDD7CF] mt-4 pt-4 space-y-2">
          {(order.discount_amount > 0 || order.rewards_discount > 0 || (order.coupon_discount || 0) > 0 || (order.card_fee || 0) > 0) && (
            <div className="flex justify-between text-sm">
              <span className="text-[#6E675F]">Subtotal</span>
              <span className="text-[#1A1A1A]">${(order.subtotal || order.total).toFixed(2)}</span>
            </div>
          )}
          {order.discount_amount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-[#2D6A4F] font-medium">Bulk discounts</span>
              <span className="text-[#2D6A4F] font-medium">−${order.discount_amount.toFixed(2)}</span>
            </div>
          )}
          {(order.coupons_applied?.length
            ? order.coupons_applied
            : (order.coupon_discount || 0) > 0
              ? [{ code: order.coupon_code, discount: order.coupon_discount }]
              : []
          ).map((c) => (
            <div key={c.code} className="flex justify-between text-sm" data-testid="confirmation-coupon-row">
              <span className="text-[#2D6A4F] font-medium">Coupon ({c.code})</span>
              <span className="text-[#2D6A4F] font-medium" data-testid="confirmation-coupon-amount">−${c.discount.toFixed(2)}</span>
            </div>
          ))}
          {(order.rewards_discount || 0) > 0 && (
            <div className="flex justify-between text-sm" data-testid="confirmation-rewards-row">
              <span className="text-[#8A6D2F] font-medium">Rewards ({order.rewards_points_used} points)</span>
              <span className="text-[#8A6D2F] font-medium" data-testid="confirmation-rewards-amount">−${order.rewards_discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm" data-testid="confirmation-shipping-row">
            <span className="text-[#6E675F]">Shipping <span data-testid="confirmation-shipping-method-label">({shippingMethodLabel(order.shipping_method)})</span></span>
            {(order.shipping_fee || 0) > 0 ? (
              <span className="text-[#1A1A1A]" data-testid="confirmation-shipping-amount">${order.shipping_fee.toFixed(2)}</span>
            ) : (
              <span className="text-[#2D6A4F] font-medium" data-testid="confirmation-shipping-amount">FREE</span>
            )}
          </div>
          {(order.package_protection || 0) > 0 && (
            <div className="flex justify-between text-sm" data-testid="confirmation-protection-row">
              <span className="text-[#6E675F]">Package Protection (signature on delivery)</span>
              <span className="text-[#1A1A1A]" data-testid="confirmation-protection-amount">${order.package_protection.toFixed(2)}</span>
            </div>
          )}
          {(order.card_fee || 0) > 0 && (
            <div className="flex justify-between text-sm" data-testid="confirmation-card-fee-row">
              <span className="text-[#6E675F]">Card processing fee</span>
              <span className="text-[#1A1A1A]" data-testid="confirmation-card-fee-amount">${order.card_fee.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold text-[#1A1A1A]">Total</span>
            <span className="text-xl font-medium text-[#1A1A1A]">${order.total.toFixed(2)} CAD</span>
          </div>
        </div>
      </div>

      <div className="text-center mt-10">
        <Link to="/my-orders" data-testid="view-my-orders-link" className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A]">
          View My Orders →
        </Link>
      </div>
    </div>
  );
}
